import { useEffect, useMemo, useState } from 'react'
import { useThemeStore } from '@/store/themeStore'
import { readPaletteHex } from '@/lib/theme'

/**
 * Фактические цвета текущей темы в виде `#rrggbb`.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ХУК. Recharts кладёт цвет в SVG-АТРИБУТЫ
 * (`stroke="…"`, `fill="…"`), а не в CSS-свойства, поэтому
 * `stroke="hsl(var(--border))"` там не разрешается вовсе — атрибут
 * получает строку, браузер её не понимает, линия не рисуется. Значит
 * цвет для графиков надо вычислить в JS и передать значением.
 *
 * ⚠️ Пересчёт привязан к `resolved` из стора темы: без этого график
 * остаётся в цветах прошлой темы до следующего ре-рендера по своей
 * причине — то есть выглядит «иногда переключается, иногда нет».
 *
 * ⚠️ Читать `getComputedStyle` синхронно в момент смены темы нельзя:
 * атрибут `data-theme` уже стоит, но стили этого кадра ещё прежние.
 * Поэтому чтение отложено на кадр.
 */

/** Роли, которые нужны графикам. Ключ — имя CSS-переменной без `--`. */
const CHART_ROLES = {
  foreground: '--foreground',
  mutedForeground: '--muted-foreground',
  border: '--border',
  card: '--card',
  background: '--background',
  primary: '--primary',
  success: '--success',
  error: '--error',
  info: '--info',
} as const

export type ThemeColors = Record<keyof typeof CHART_ROLES, string> & { isDark: boolean }

/** Значения светлой темы: используются до первого чтения и в SSR/тестах. */
const FALLBACK: Record<keyof typeof CHART_ROLES, string> = {
  foreground: '#212121',
  mutedForeground: '#616161',
  border: '#e0e0e0',
  card: '#ffffff',
  background: '#f5f5f5',
  primary: '#ffc107',
  success: '#4caf50',
  error: '#f44336',
  info: '#2196f3',
}

function readAll(): Record<keyof typeof CHART_ROLES, string> {
  const out = { ...FALLBACK }
  for (const [key, variable] of Object.entries(CHART_ROLES) as [keyof typeof CHART_ROLES, string][]) {
    const hex = readPaletteHex(variable)
    if (hex) out[key] = hex
  }
  return out
}

export function useThemeColors(): ThemeColors {
  const resolved = useThemeStore((state) => state.resolved)
  const [colors, setColors] = useState<Record<keyof typeof CHART_ROLES, string>>(() =>
    typeof document === 'undefined' ? FALLBACK : readAll()
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const frame = window.requestAnimationFrame(() => setColors(readAll()))
    return () => window.cancelAnimationFrame(frame)
  }, [resolved])

  return useMemo(() => ({ ...colors, isDark: resolved === 'dark' }), [colors, resolved])
}

/**
 * Готовые пропсы «обвязки» графика: сетка, оси, тултип.
 *
 * Заведён отдельно, чтобы не расходились восемь файлов с `recharts`
 * (`RevenueChart`, `VolumeChart`, `ConversionChart`, `ClientBaseCard`,
 * `SalesFunnel`, `PartnerMarketingPage`, две игровые). До этого сетка и оси
 * рисовались умолчаниями recharts (серое `#ccc` и `#666`), то есть были
 * зашиты в библиотеку и в тёмной теме читались как грязь.
 *
 * ⚠️ Цвета СЕРИЙ данных сюда не входят намеренно: они несут смысл
 * («выручка», «лиды») и подбираются на месте. Их задача — быть различимыми
 * между собой, а не следовать теме.
 */
export function useChartAxisTheme() {
  const c = useThemeColors()
  return useMemo(
    () => ({
      /** <CartesianGrid {...grid} /> */
      grid: { stroke: c.border, strokeDasharray: '3 3' as const },
      /** <XAxis {...axis} /> и <YAxis {...axis} /> */
      axis: {
        stroke: c.border,
        tick: { fill: c.mutedForeground, fontSize: 11 },
      },
      /** <Tooltip contentStyle={tooltip} /> для стандартного тултипа recharts. */
      tooltip: {
        backgroundColor: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        color: c.foreground,
      },
      /** Курсор наведения (вертикальная подсветка). */
      cursor: { stroke: c.border, strokeWidth: 1 },
      isDark: c.isDark,
    }),
    [c]
  )
}

/**
 * Счётчик, растущий ПОСЛЕ того, как новая тема реально применилась к стилям.
 *
 * ⚠️ Нужен всем резолверам ниже: в момент смены темы атрибут `data-theme`
 * на `<html>` уже новый, а вычисленные стили этого кадра — ещё прежние,
 * поэтому синхронное чтение вернуло бы палитру прошлой темы.
 */
function useAppliedThemeTick(): number {
  const resolved = useThemeStore((state) => state.resolved)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const frame = window.requestAnimationFrame(() => setTick((n) => n + 1))
    return () => window.cancelAnimationFrame(frame)
  }, [resolved])
  return tick
}

/**
 * Любая переменная палитры значением — для мест, где цвет обязан быть
 * строкой, а не классом: SVG-атрибуты recharts, `<canvas>`, inline-стили.
 *
 * `const color = useThemeVarResolver()` →
 * `stroke={color('--border', '#E0E0E0')}`
 *
 * ⚠️ Для цветов СЕРИЙ это не годится — им нужен `useChartPalette()` ниже.
 * Серия, заведённая как переменная темы, зеркалится вместе с ней: линия
 * CPL на графике маркетинга брала `--mkt-highlight` и уходила в #6D00AC,
 * то есть 1,53:1 на тёмной карточке.
 *
 * Второй аргумент обязателен: переменной может не оказаться (SSR, тест,
 * опечатка в имени), и молча подставленный чёрный хуже явного запасного.
 */
export function useThemeVarResolver(): (variableName: string, fallback: string) => string {
  const tick = useAppliedThemeTick()
  return useMemo(() => {
    void tick
    return (variableName: string, fallback: string) => readPaletteHex(variableName) ?? fallback
  }, [tick])
}

/**
 * Произвольный оттенок палитровой лестницы значением — для серий данных.
 * `palette('emerald', 500)` → `#10b981` в светлой теме и зеркальный оттенок
 * в тёмной, ровно как у одноимённого класса Tailwind.
 */
export function useChartPalette(): (ramp: string, shade: number, fallback?: string) => string {
  const tick = useAppliedThemeTick()
  return useMemo(() => {
    void tick
    return (ramp: string, shade: number, fallback = '#888888') =>
      readPaletteHex(`--c-${ramp}-${shade}`) ?? fallback
  }, [tick])
}
